import { useEffect, useRef, useState } from 'react'
import { CONTACTS } from './constants.js'
import { sendGoal } from './analytics.js'
import { mediaAssets } from './media.js'
import { registrationRoutes, resolveRegistration } from './registrationRoutes.js'
import './registration.css'

function RouteSteps({ route }) {
  return (
    <ol className="registration-steps">
      {route.steps.map(([title, text]) => <li key={title}><strong>{title}</strong><p>{text}</p></li>)}
    </ol>
  )
}

function CargoHelp() {
  return <details className="registration-help"><summary>Хочу подключить грузовой тариф</summary>
    <p>Сначала заполните подходящую анкету Яндекс Доставки. Затем отправьте парку СТС и параметры кузова: внутренние длину, ширину и высоту грузового отсека. Парк проверит подключение тарифа для вашего автомобиля.</p>
    <a href={CONTACTS.max} target="_blank" rel="noopener noreferrer">Написать парку в MAX ↗</a>
  </details>
}

export default function RegistrationChooser() {
  const [service, setService] = useState(null)
  const [smz, setSmz] = useState(null)
  const heading = useRef(null)
  const changed = useRef(false)
  const route = resolveRegistration(service, smz)
  const taxiWithoutSmz = service === 'taxi' && smz === 'no'
  const page = () => ({ page: window.location.pathname })

  useEffect(() => {
    if (changed.current) heading.current?.focus({ preventScroll: true })
  }, [service, smz])

  function chooseService(value) {
    changed.current = true
    setService(value)
    setSmz(null)
    sendGoal('registration_choice', { ...page(), step: 'service', service: value })
  }

  function chooseSmz(value) {
    changed.current = true
    setSmz(value)
    sendGoal('registration_choice', { ...page(), step: 'smz', service, smz: value })
    const result = resolveRegistration(service, value)
    sendGoal('registration_result', { ...page(), route: result?.id || 'taxi_smz_required' })
  }

  function switchToDelivery() {
    changed.current = true
    setService('delivery')
    setSmz('no')
    sendGoal('registration_choice', { ...page(), step: 'taxi_alternative', service: 'delivery', smz: 'no' })
    sendGoal('registration_result', { ...page(), route: 'delivery_without_smz' })
  }

  function back() {
    changed.current = true
    if (smz !== null) setSmz(null)
    else setService(null)
  }

  const title = route ? route.title : taxiWithoutSmz ? 'В такси подключаем с самозанятостью' : service ? 'Есть самозанятость?' : 'Как хотите работать?'

  return <section className="registration" id="registration" aria-labelledby="registration-heading">
    <div className="registration-guide">
      <img src={mediaAssets.avatar.src} width="56" height="56" alt="Лена" />
      <p><strong>Лена поможет начать</strong><span>Два ответа — и покажу анкету для вас.</span></p>
    </div>
    <div className="registration-topline">
      <span>{route || taxiWithoutSmz ? 'Ваш вариант' : service ? 'Вопрос 2 из 2' : 'Вопрос 1 из 2'}</span>
      {service && <button type="button" className="registration-back" onClick={back}>← {smz !== null ? 'Изменить ответ' : 'Назад'}</button>}
    </div>
    <h2 id="registration-heading" ref={heading} tabIndex={-1}>{title}</h2>
    {!service ? <div className="registration-options">
      <button type="button" onClick={() => chooseService('delivery')}><strong>Доставка</strong><span>Пешком, на велосипеде или авто. С СМЗ и без.</span><b aria-hidden="true">→</b></button>
      <button type="button" onClick={() => chooseService('taxi')}><strong>Такси</strong><span>Заказы с пассажирами. Только с СМЗ.</span><b aria-hidden="true">→</b></button>
    </div> : smz === null ? <>
      <p className="registration-explainer">{service === 'taxi' ? 'Для подключения в такси через наш парк нужна самозанятость.' : 'В Доставку можно с самозанятостью и без неё — отличаются анкеты и комиссия.'} СМЗ — сокращение от «самозанятый».</p>
      <div className="registration-options">
        <button type="button" onClick={() => chooseSmz('yes')}><strong>Да, есть СМЗ</strong><span>Уже оформлена самозанятость.</span><b aria-hidden="true">→</b></button>
        <button type="button" onClick={() => chooseSmz('no')}><strong>Нет СМЗ</strong><span>Самозанятость не оформлена.</span><b aria-hidden="true">→</b></button>
      </div>
    </> : taxiWithoutSmz ? <div className="registration-result">
      <p>Без СМЗ сейчас можно подключиться к Яндекс Доставке: пешком, на велосипеде или автомобиле. Для этого есть отдельная анкета.</p>
      <button type="button" className="button registration-cta" onClick={switchToDelivery}>Выбрать Доставку без СМЗ <span aria-hidden="true">→</span></button>
      <p className="registration-explainer">Если хотите именно такси, сначала оформите самозанятость. Затем вернитесь и выберите «Да, есть СМЗ».</p>
    </div> : <div className="registration-result">
      <p className="registration-benefit">{route.benefit}</p>
      <p className="registration-terms">{route.terms}</p>
      <a className="button registration-cta" href={route.href} target="_blank" rel="noopener noreferrer"
        onClick={() => sendGoal(route.service === 'taxi' ? 'fleet_click' : 'delivery_form_click', { ...page(), route: route.id, label: route.label })}>
        <span>{route.label}</span><span aria-hidden="true">↗</span>
      </a>
      <p className="registration-explainer">Откроется анкета Яндекса в новой вкладке. Эти шаги останутся здесь.</p>
      <h3>Что делать после анкеты</h3>
      <RouteSteps route={route} />
      {route.service === 'delivery' && <CargoHelp />}
    </div>}
    {!route && <details className="registration-help">
      <summary>Уже отправили анкету?</summary>
      <p>Не заполняйте её повторно. Войдите в Яндекс Про с тем же номером: для Доставки выберите ABG logist, для такси — ABG Taxi. Если нужного парка нет, напишите нам — проверим подключение.</p>
      <a href={CONTACTS.max} target="_blank" rel="noopener noreferrer">Нужна помощь парка ↗</a>
    </details>}
    {route && <a className="registration-contact" href={CONTACTS.max} target="_blank" rel="noopener noreferrer">Не получается подключиться? Написать парку ↗</a>}
    <noscript><div className="registration-fallback">
      <h3>Выберите подходящую анкету</h3>
      <p>Такси — только с самозанятостью. Для Доставки доступны оба варианта.</p>
      {Object.values(registrationRoutes).map(item => <section key={item.id}><h3>{item.title}</h3><p>{item.benefit}. {item.terms}</p>
        <a href={item.href} target="_blank" rel="noopener noreferrer">{item.label} ↗</a><RouteSteps route={item} /></section>)}
      <CargoHelp />
    </div></noscript>
  </section>
}
